import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { fileToDataUrl, stripDataUrl, downloadB64 } from "../../lib/files";
import { usePolling } from "../../lib/usePolling";
import { toast } from "sonner";
import { Download, Upload, MessageSquarePlus, Package } from "lucide-react";

export default function AgentProgetti() {
  const [list, setList] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [open, setOpen] = useState(null);
  const [ticket, setTicket] = useState({ subject: "", body: "" });

  const load = async () => {
    try { const { data } = await http.get("/crm/me/projects"); setList(data); setBlocked(false); }
    catch (e) { if (e?.response?.status === 403) { setBlocked(true); setList([]); } else toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const uploadMaterial = async (pid, key, e) => {
    const f = e.target.files?.[0];
    try {
      let data_b64 = null, filename = null;
      if (f) { const d = await fileToDataUrl(f); data_b64 = stripDataUrl(d); filename = f.name; }
      await http.post(`/crm/me/projects/${pid}/materials`, { key, collected: true, filename, data_b64 });
      await load(); toast.success(`Materiale "${key}" registrato.`);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const dossier = async (pid, scope = "admin") => {
    try { const { data } = await http.get(`/crm/me/projects/${pid}/dossier`, { params: { scope } }); downloadB64(data.data_b64, data.filename, data.content_type); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const sendTicket = async (pid) => {
    if (!ticket.subject.trim()) return toast.error("Oggetto richiesto.");
    try { await http.post(`/crm/me/projects/${pid}/tickets`, ticket); setTicket({ subject: "", body: "" }); toast.success("Richiesta di assistenza inviata."); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (blocked) return <div data-testid="agent-projects-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center"><h1 className="font-serif text-2xl mb-2">Progetti</h1><p className="text-sm text-neutral-600">Disponibili dopo l'attivazione del tuo account.</p></div>;
  if (list === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  return (
    <div data-testid="agent-projects-page" className="max-w-5xl mx-auto">
      <h1 className="font-serif text-3xl mb-1">Progetti / Lavorazione</h1>
      <p className="text-sm text-neutral-500 mb-5">Raccogli i materiali del cliente, consulta gli stati e gli URL, apri richieste di assistenza, scarica il dossier.</p>
      <div className="space-y-3" data-testid="agent-projects-list">
        {list.length === 0 && <div className="card-clean p-6 text-sm text-neutral-500">Nessun progetto assegnato.</div>}
        {list.map((p) => {
          const got = new Set((p.materials || []).filter((m) => m.collected).map((m) => m.key));
          return (
            <div key={p.id} className="card-clean p-4" data-testid={`agent-project-${p.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="font-semibold">{p.salon_name}</div>
                  <div className="text-xs text-neutral-500">Stato: <strong>{p.state}</strong> · Revisioni: {p.revision_count}/2</div>
                </div>
                <button onClick={() => dossier(p.id)} data-testid={`agent-project-dossier-${p.id}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Package className="w-3 h-3" /> Dossier</button>
                <button onClick={() => dossier(p.id, "cliente")} data-testid={`agent-project-dossier-cliente-${p.id}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Package className="w-3 h-3" /> Dossier cliente</button>
                <button onClick={() => setOpen(open === p.id ? null : p.id)} data-testid={`agent-project-open-${p.id}`} className="px-3 py-1.5 rounded-md text-white text-xs" style={{ background: "var(--brand-dark)" }}>{open === p.id ? "Chiudi" : "Apri"}</button>
              </div>
              {open === p.id && (
                <div className="mt-3 border-t pt-3 space-y-4" style={{ borderColor: "var(--border-silver)" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Materiali cliente</div>
                    <div className="flex flex-wrap gap-2">
                      {(p.required_materials || []).map((key) => (
                        <label key={key} className={`px-3 py-1.5 rounded-md border text-xs flex items-center gap-1 cursor-pointer ${got.has(key) ? "bg-green-50 border-green-300 text-green-700" : ""}`} style={!got.has(key) ? { borderColor: "var(--border-silver)" } : {}} data-testid={`agent-mat-${p.id}-${key}`}>
                          {got.has(key) ? "✓ " : <Upload className="w-3 h-3" />} {key}
                          <input type="file" className="hidden" onChange={(e) => uploadMaterial(p.id, key, e)} />
                        </label>
                      ))}
                    </div>
                  </div>
                  {(p.urls && Object.keys(p.urls).length > 0) && (
                    <div className="text-xs">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">URL</div>
                      {["draft_url", "review_url", "final_url"].map((k) => p.urls[k] && <div key={k}><span className="text-neutral-500">{k}: </span><a href={p.urls[k]} target="_blank" rel="noreferrer" className="underline">{p.urls[k]}</a></div>)}
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Assistenza</div>
                    <div className="flex gap-2 flex-wrap">
                      <input placeholder="Oggetto" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} data-testid={`agent-ticket-subject-${p.id}`} className="flex-1 min-w-40 px-3 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }} />
                      <button onClick={() => sendTicket(p.id)} data-testid={`agent-ticket-send-${p.id}`} className="px-3 py-2 rounded-md text-white text-xs flex items-center gap-1" style={{ background: "var(--brand-primary)" }}><MessageSquarePlus className="w-3 h-3" /> Invia</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
